/**
 * Prototype-only dataset-level metadata keyed by `dataset_ref`.
 *
 * These fields are NOT part of the authoritative backend contract. They are
 * a frontend mock that lets `/datasets` cards communicate what a logical
 * dataset *is for*, what it predicts, what schema it expects, and which
 * model architectures are technically compatible. The 3010 Mock UI uses
 * these to explain the site-local product direction to the frontend team
 * before any of this lands in real backend code.
 *
 * Key product-vocabulary choices encoded here:
 *
 *   - `expected_schema` is a *requirement* the dataset places on each
 *     participating site, NOT a per-site validation result. Whether a
 *     site actually satisfies the schema is decided at run validation
 *     time, not in this registry.
 *
 *   - `compatible_models` lists model architectures that are technically
 *     appropriate for the dataset's structure and task. It is NOT a
 *     "recommended" or "best" model — there is no benchmark evidence
 *     ranking these. UI may surface `default_model` as a sensible
 *     starting point, never as a winner.
 *
 *   - `task_chips` is the small set of badges shown on the card. They
 *     describe the dataset *category*, not the run status.
 *
 * Adding a new logical dataset: register its `dataset_ref` here with the
 * same shape and the `/datasets` card picks it up automatically. Missing
 * entries fall back to a slim view — the page still renders.
 */

/**
 * @typedef {Object} DatasetDefinition
 * @property {string} display_name
 * @property {string} description       Short 1-2 sentence summary, written for product audience.
 * @property {string} task_summary      Compact natural-language task description.
 * @property {string} target_variable   What the model predicts.
 * @property {string} expected_schema   Schema label / version the dataset requires from participating sites.
 * @property {string} data_unit         Granularity / format of one example (e.g. "Patch-level CT", "Pixel-level mask").
 * @property {string[]} compatible_models  Model architectures technically appropriate for this dataset/task.
 * @property {string=} default_model    Optional default starting point. NOT a recommendation.
 *
 * NOTE: The previous `task_chips` field has been removed. Task chips on the
 * `/datasets` card are derived directly from the capability's `tasks_supported`
 * array so the taxonomy stays consistent (no standalone "Imaging" label).
 */

/** @type {Record<string, DatasetDefinition>} */
export const mockDatasetDefinitions = {
  "tabular/primary_site_oncology_v1": {
    display_name: "Primary Site Oncology",
    description:
      "Predicts the cancer primary site from structured oncology records (demographics + tumour markers + treatment history) across participating sites.",
    task_summary: "Tabular classification",
    target_variable: "Primary site",
    expected_schema: "Oncology tabular v1",
    data_unit: "Patient-level tabular rows",
    compatible_models: ["Logistic Regression", "MLP", "TabNet", "FT-Transformer"],
    default_model: "Logistic Regression",
  },

  "tabular/omop_tumor_cell_range_v1": {
    display_name: "OMOP Tumour Cell Range",
    description:
      "OMOP-aligned cohort capturing tumour cell range characteristics, demographics, age, and biomarker status (incl. ER status). Predicts oestrogen-receptor (ER) status from a 39-feature tumour profile.",
    task_summary: "Tabular classification",
    target_variable: "ER status (positive / negative)",
    expected_schema: "OMOP tumour profile v1 (39 features incl. ER status)",
    data_unit: "Patient-level tabular rows",
    compatible_models: ["Logistic Regression", "MLP", "TabNet", "FT-Transformer"],
    default_model: "Logistic Regression",
  },

  "tabular/brca_survival_v1": {
    display_name: "BRCA Survival",
    description:
      "Breast cancer cohort enriched with genomic instability scores (aneuploidy, MSI) alongside clinical covariates. Predicts patient survival outcome from a 64-feature profile.",
    task_summary: "Tabular classification",
    target_variable: "Survival outcome (binary)",
    expected_schema: "BRCA panel v1 (64 features incl. aneuploidy, MSI)",
    data_unit: "Patient-level tabular rows (with genomic scores)",
    compatible_models: ["Logistic Regression", "MLP", "TabNet", "FT-Transformer"],
    default_model: "MLP",
  },

  "tabular/therapy_type_v1": {
    display_name: "Therapy Type Classification",
    description:
      "Multi-class classification of the therapy regimen prescribed to oncology patients, based on a 37-feature structured clinical profile.",
    task_summary: "Tabular multi-class classification",
    target_variable: "Therapy regimen (multi-class)",
    expected_schema: "Therapy type tabular v1 (37 features)",
    data_unit: "Patient-level tabular rows",
    compatible_models: ["Logistic Regression", "MLP", "TabNet", "FT-Transformer"],
    default_model: "MLP",
  },

  "tabular/treatment_outcome_v1": {
    display_name: "Treatment Outcome",
    description:
      "Predicts treatment outcome from a 36-feature clinical profile. Acts as the default tabular dataset for staged Flower pyproject runs.",
    task_summary: "Tabular classification",
    target_variable: "Treatment outcome (binary)",
    expected_schema: "Treatment outcome tabular v1 (36 features)",
    data_unit: "Patient-level tabular rows",
    compatible_models: ["Logistic Regression", "MLP", "TabNet", "FT-Transformer"],
    default_model: "Logistic Regression",
  },

  "prostatex_seg_v1": {
    display_name: "ProstateX Segmentation",
    description:
      "Pixel-level prostate gland and lesion segmentation from multi-parametric MRI volumes (T2 + ADC + DWI). Used to delineate suspected tumour regions for radiology review.",
    task_summary: "3D imaging segmentation",
    target_variable: "Prostate / lesion mask",
    expected_schema: "ProstateX mp-MRI v1 (T2 · ADC · DWI)",
    data_unit: "Volumetric MRI · pixel-level mask",
    compatible_models: ["UNet", "UNet++", "FPN"],
    default_model: "UNet",
  },

  "prostatex_cls_v1": {
    display_name: "ProstateX Classification",
    description:
      "Predicts clinically significant prostate cancer from biparametric MRI volumes (T2 + ADC). Patient-level binary outcome.",
    task_summary: "3D imaging classification",
    target_variable: "Clinically significant cancer (binary)",
    expected_schema: "ProstateX bi-MRI v1 (T2 · ADC)",
    data_unit: "Patient-level binary outcome",
    // MultiModalFusionNet temporarily excluded from the demo-visible list
    // pending an unresolved end-to-end issue uncovered in 2026-06-09 Phase
    // B testing (3/4 other models passed — ResNet18_3D / ConvNeXt_3D /
    // SwinTiny_3D — only MMFusion did not). Restore once backend issue
    // identified + fixed.
    compatible_models: ["SimpleCNN", "ResNet18_3D", "ConvNeXt_3D", "SwinTiny_3D"],
    default_model: "ResNet18_3D",
  },

  "lidc_cls_v1": {
    display_name: "LIDC-IDRI Lung Nodule Patches",
    description:
      "Classifies pulmonary nodule patches from chest CT as benign vs. malignant. Patch-level binary outcome.",
    task_summary: "Imaging classification",
    target_variable: "Nodule malignancy (binary)",
    expected_schema: "LIDC patch v1 (32×32 CT patches + radiologist label)",
    data_unit: "Patch-level CT (32×32)",
    compatible_models: ["ConvNeXt", "ResNet18", "Swin Transformer"],
    default_model: "ResNet18",
  },

  "lidc_seg_v1": {
    display_name: "LIDC-IDRI Volumetric Segmentation",
    description:
      "Pulmonary nodule segmentation on volumetric chest CT scans paired with radiologist contour masks.",
    task_summary: "3D imaging segmentation",
    target_variable: "Nodule mask",
    expected_schema: "LIDC volumetric v1 (CT volume + contour mask)",
    data_unit: "Volumetric CT · pixel-level mask",
    compatible_models: ["UNet", "UNet++", "FPN"],
    default_model: "UNet",
  },
};

/**
 * Structured target schemas per `dataset_ref`, used by the schema-validation
 * preflight (mockSchemaValidation.js) to compare each participating site's
 * local data against the dataset's expected schema version.
 *
 * Mock-only. Mirrors the *shape* of what a real backend schema would carry
 * (see Backend_To_Frontend/2026-05-26_tabular_schema_validation_preflight_sync.md
 * and data_review_popup.html), not its exact byte values. The validator uses:
 *   - `feature_dim` / `num_classes` for shape-signature checks
 *   - `label_space` / `label_mapping` for label-space conformance
 *   - `sample_features` only for display in the data-review card
 *
 * Per-site deviations (extra/missing columns, out-of-range labels, etc.) are
 * NOT stored here — they live in CAPABILITY_DEVIATIONS inside the validator,
 * so a dataset_ref describes the *target* and the deviation map describes how
 * a specific site diverges from it.
 *
 * @typedef {Object} DatasetSchema
 * @property {string} schema_id
 * @property {"tabular"|"imaging"} modality
 * @property {number} num_classes
 * @property {string[]} label_space
 * @property {Record<string, number>} label_mapping
 * @property {number=} feature_dim         tabular only — number of feature columns
 * @property {string[]=} sample_features   tabular only — representative subset for display
 * @property {string=} input_spec          imaging only — e.g. "32×32 CT patch"
 * @property {number=} channels            imaging only — input channel count
 */

const tabularLabelMapping = (labels) =>
  labels.reduce((acc, name, i) => {
    acc[name] = i;
    return acc;
  }, {});

/** @type {Record<string, DatasetSchema>} */
export const DATASET_SCHEMAS = {
  "tabular/primary_site_oncology_v1": {
    schema_id: "primary_site_oncology_v1",
    modality: "tabular",
    feature_dim: 36,
    num_classes: 4,
    label_space: ["Lung", "Breast", "Colorectal", "Prostate"],
    label_mapping: tabularLabelMapping(["Lung", "Breast", "Colorectal", "Prostate"]),
    sample_features: [
      "age",
      "sex",
      "tumor_marker_cea",
      "ajcc_pathologic_tumor_stage",
      "fraction_genome_altered",
      "msi_score",
    ],
  },
  "tabular/omop_tumor_cell_range_v1": {
    schema_id: "omop_tumor_cell_range_v1",
    modality: "tabular",
    feature_dim: 39,
    num_classes: 2,
    label_space: ["ER-negative", "ER-positive"],
    label_mapping: tabularLabelMapping(["ER-negative", "ER-positive"]),
    sample_features: [
      "age",
      "race",
      "er_status",
      "tumor_cell_range_low",
      "tumor_cell_range_high",
      "biomarker_status",
    ],
  },
  "tabular/brca_survival_v1": {
    schema_id: "dataset_brca_survival_v1",
    modality: "tabular",
    feature_dim: 61,
    num_classes: 2,
    label_space: ["Alive", "Deceased"],
    label_mapping: tabularLabelMapping(["Alive", "Deceased"]),
    sample_features: [
      "MSI_SCORE_MANTIS",
      "TBL_SCORE",
      "RACE_Missing",
      "AGE",
      "AJCC_PATHOLOGIC_TUMOR_STAGE",
      "FRACTION_GENOME_ALTERED",
    ],
  },
  "tabular/therapy_type_v1": {
    schema_id: "therapy_type_v1",
    modality: "tabular",
    feature_dim: 37,
    num_classes: 5,
    label_space: ["Chemotherapy", "Radiation", "Surgery", "Immunotherapy", "Hormonal"],
    label_mapping: tabularLabelMapping([
      "Chemotherapy",
      "Radiation",
      "Surgery",
      "Immunotherapy",
      "Hormonal",
    ]),
    sample_features: [
      "age",
      "ecog_status",
      "tumor_stage",
      "prior_therapy_count",
      "biomarker_panel",
      "comorbidity_index",
    ],
  },
  "tabular/treatment_outcome_v1": {
    schema_id: "treatment_outcome_v1",
    modality: "tabular",
    feature_dim: 36,
    num_classes: 2,
    label_space: ["No response", "Response"],
    label_mapping: tabularLabelMapping(["No response", "Response"]),
    sample_features: [
      "age",
      "baseline_tumor_burden",
      "treatment_line",
      "performance_status",
      "biomarker_score",
      "prior_response",
    ],
  },
  prostatex_seg_v1: {
    schema_id: "prostatex_seg_v1",
    modality: "imaging",
    input_spec: "Volumetric mp-MRI (T2 · ADC · DWI)",
    channels: 3,
    num_classes: 2,
    label_space: ["background", "lesion"],
    label_mapping: { background: 0, lesion: 1 },
  },
  prostatex_cls_v1: {
    schema_id: "prostatex_cls_v1",
    modality: "imaging",
    input_spec: "Volumetric bi-MRI (T2 · ADC)",
    channels: 2,
    num_classes: 2,
    label_space: ["benign", "clinically_significant"],
    label_mapping: { benign: 0, clinically_significant: 1 },
  },
  lidc_cls_v1: {
    schema_id: "lidc_cls_v1",
    modality: "imaging",
    input_spec: "32×32 CT patch",
    channels: 1,
    num_classes: 2,
    label_space: ["benign", "malignant"],
    label_mapping: { benign: 0, malignant: 1 },
  },
  lidc_seg_v1: {
    schema_id: "lidc_seg_v1",
    modality: "imaging",
    input_spec: "Volumetric CT + contour mask",
    channels: 1,
    num_classes: 2,
    label_space: ["background", "nodule"],
    label_mapping: { background: 0, nodule: 1 },
  },
};

/** Lookup a structured schema by dataset_ref. Returns null if unknown. */
export const getDatasetSchema = (datasetRef) => DATASET_SCHEMAS[datasetRef] || null;

/**
 * Map a capability `readiness_status` to a user-facing **capability status**
 * label. This rephrasing is deliberate: the registry must not claim to have
 * run schema validation on each site — that happens during run pre-checks.
 * `Available` / `Needs review` / `Warming up` describe **registration** /
 * platform-side capability state, not validation outcome.
 *
 * @param {string=} status raw readiness_status from a SiteDatasetCapability
 * @returns {{label: string, tone: "ok"|"warn"|"info"|"neutral"}}
 */
export const capabilityStatusFromReadiness = (status) => {
  switch (status) {
    case "ready":
      return { label: "Available", tone: "ok" };
    case "warming_up":
      return { label: "Warming up", tone: "info" };
    case "needs_attention":
      return { label: "Needs review", tone: "warn" };
    case "unsupported":
      return { label: "Unavailable", tone: "neutral" };
    default:
      return { label: "Unknown", tone: "neutral" };
  }
};
